/**
 * Just added (D224): the list under the Add box of everything typed or captured and not yet
 * acknowledged. The server owns it (`GET /api/just-added`), so it survives a reload and shows on
 * every device; this hook reads it, polls while the AI is on anything, and holds a placeholder for
 * an entry the outbox has not delivered yet (offline, or waiting on a position fix).
 *
 * Nothing here forwards anything to the Inbox. An entry leaves when it is acknowledged: filed ones
 * disappear (what they made stays), unplaced ones move to the Inbox on the server.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  ApiError,
  api,
  type CaptureTier,
  type InboxAiCreated,
  type InboxAiRetryResult,
  type InboxItem,
  type InboxRefineBody,
  type JustAddedEntry,
  type JustAddedState,
  type RecurrenceRequest,
  type Task,
} from "./api";
import type { ResolveRequest } from "./components/InboxSection";
import { ITEM_EDITED_EVENT } from "./editPopupEvents";
import { postNotice } from "./notices";
import type { AddBin } from "./addBin";

/** What the rows need of an entry. `sending`: the outbox has it but the server does not yet. */
export type AddEntry = {
  id: string;
  text: string;
  state: JustAddedState | "sending";
  created: InboxAiCreated | null;
  /** The AI is still polishing the task it made. */
  enriching: boolean;
  /** Why the AI was unsure, or why nothing ran it. */
  reason: string | null;
  /** The plain-language reason the AI failed. */
  error: string | null;
  /** The whole entry as the server has it - what an Inbox row's forms read. `null` for a line the
   * outbox still holds. */
  item: JustAddedEntry | null;
};

export type Kind = "task" | "note" | "project" | "context";

const POLL_MS = 2500;
/** How often a list with nothing in flight is re-read, while the tab is visible. */
const IDLE_POLL_MS = 10_000;
/** A placeholder the server never produced in this long is given up on: the outbox says so itself. */
const GIVE_UP_MS = 10 * 60 * 1000;

function toEntry(entry: JustAddedEntry): AddEntry {
  return {
    id: entry.id,
    text: entry.raw_text,
    state: entry.state,
    created: entry.created,
    enriching: entry.enriching,
    reason: entry.reason,
    error: entry.last_error ?? null,
    item: entry,
  };
}

export type JustAddedActions = {
  /** The Inbox row's calls, on an entry: its forms file it by hand, its bulbs ask the AI. */
  resolve: (
    item: InboxItem,
    request: ResolveRequest,
    recurrence?: RecurrenceRequest,
    /** The entry is filed already ("Change to"): what it made is taken back with the save. */
    replace?: boolean,
  ) => Promise<void>;
  retryAi: (item: InboxItem, tier: CaptureTier | null) => Promise<InboxAiRetryResult>;
  refineAi: (
    item: InboxItem,
    body: InboxRefineBody,
    recurrence?: RecurrenceRequest,
    replace?: boolean,
  ) => Promise<InboxAiRetryResult>;
  settled: (item: InboxItem, result: InboxAiRetryResult | null) => Promise<void>;
};

export function useJustAdded(onFiled?: () => void): {
  entries: AddEntry[];
  /** An entry the outbox holds, shown until the server has it. */
  track: (entry: { id: string; bin: AddBin; text: string }) => void;
  refresh: () => Promise<void>;
  /** The person has seen these (all of them, if no ids). */
  acknowledge: (ids?: string[]) => Promise<void>;
  actions: JustAddedActions;
} {
  const [server, setServer] = useState<AddEntry[]>([]);
  const [placeholders, setPlaceholders] = useState<
    Map<string, { text: string; addedAt: number }>
  >(new Map());
  const alive = useRef(true);
  const loaded = useRef(false);
  // Entries whose row is showing an AI answer: kept as they were, so the answer is not unmounted
  // under the reader when the server moves the entry on. Released when the answer is cleared.
  const held = useRef(new Map<string, AddEntry>());
  const filedSeen = useRef(new Set<string>());
  const serverSeen = useRef(new Set<string>());
  const onFiledRef = useRef(onFiled);
  onFiledRef.current = onFiled;
  const serverRef = useRef(server);
  serverRef.current = server;
  // Bumped by every read and every acknowledge: an answer from an older read is dropped, so a
  // poll still in flight when a line was let go cannot bring the line back.
  const generation = useRef(0);

  useEffect(() => {
    alive.current = true;
    return () => {
      alive.current = false;
    };
  }, []);

  const refresh = useCallback(async () => {
    const mine = ++generation.current;
    try {
      const { items } = await api.justAdded();
      if (!alive.current || mine !== generation.current) return;
      setServer(items.map((item) => held.current.get(item.id) ?? toEntry(item)));
      let newlyFiled = false;
      for (const item of items) {
        serverSeen.current.add(item.id);
        if (item.state === "filed" && !filedSeen.current.has(item.id)) {
          filedSeen.current.add(item.id);
          newlyFiled = true;
        }
      }
      // The first read only learns what is already there; later ones announce what has just been made.
      if (newlyFiled && loaded.current) onFiledRef.current?.();
      loaded.current = true;
      setPlaceholders((current) => {
        const ids = new Set(items.map((item) => item.id));
        const now = Date.now();
        const next = new Map(
          [...current].filter(
            ([id, held]) =>
              !ids.has(id) && !serverSeen.current.has(id) && now - held.addedAt < GIVE_UP_MS,
          ),
        );
        return next.size === current.size ? current : next;
      });
    } catch (err) {
      if (err instanceof ApiError && err.isAuth) return; // AuthGate takes over
      // Any other failure is the same as far as a glance at progress goes: try again next tick.
    }
  }, []);

  useEffect(() => {
    void refresh();
    const onVisible = () => {
      if (document.visibilityState === "visible") void refresh();
    };
    const onEdited = () => void refresh();
    document.addEventListener("visibilitychange", onVisible);
    window.addEventListener("focus", onVisible);
    // An edit popup saved, or deleted, something a line made.
    window.addEventListener(ITEM_EDITED_EVENT, onEdited);
    return () => {
      document.removeEventListener("visibilitychange", onVisible);
      window.removeEventListener("focus", onVisible);
      window.removeEventListener(ITEM_EDITED_EVENT, onEdited);
    };
  }, [refresh]);

  // Newest first, by id: ids are ULIDs minted when the line was typed, so the newest line sits
  // next to the box it was typed in (where it shows the box is working), and nothing moves as
  // lines change state or the outbox hands them over.
  const entries: AddEntry[] = [
    ...[...placeholders]
      .filter(([id]) => !server.some((entry) => entry.id === id))
      .map(([id, held]) => ({
        id,
        text: held.text,
        state: "sending" as const,
        created: null,
        enriching: false,
        reason: null,
        error: null,
        item: null,
      })),
    ...server,
  ].sort((a, b) => (a.id < b.id ? 1 : a.id > b.id ? -1 : 0));

  const busy = entries.some(
    (entry) =>
      entry.state === "sending" ||
      entry.state === "queued" ||
      entry.state === "processing" ||
      entry.enriching,
  );
  const listed = entries.length > 0;
  useEffect(() => {
    if (!busy && !listed) return;
    // Quickly while the AI is on something; slowly otherwise, because what a line made can change
    // under it (edited in another tab, deleted, polished) and a row left stale is a row mistrusted.
    const timer = window.setInterval(
      () => {
        if (busy || document.visibilityState === "visible") void refresh();
      },
      busy ? POLL_MS : IDLE_POLL_MS,
    );
    return () => window.clearInterval(timer);
  }, [busy, listed, refresh]);

  const track = useCallback((entry: { id: string; bin: AddBin; text: string }) => {
    setPlaceholders((current) =>
      new Map(current).set(entry.id, { text: entry.text, addedAt: Date.now() }),
    );
    void refresh();
  }, [refresh]);

  const acknowledge = useCallback(
    async (ids?: string[]) => {
      // Gone at once; the server confirms (and an entry the AI is still on comes back).
      generation.current++;
      setServer((current) =>
        current.filter(
          (entry) =>
            (ids ? !ids.includes(entry.id) : false) ||
            entry.state === "queued" ||
            entry.state === "processing",
        ),
      );
      try {
        await api.acknowledgeJustAdded(ids);
      } catch (err) {
        postNotice(err instanceof ApiError ? err.message : "Couldn't acknowledge that.");
      }
      await refresh();
    },
    [refresh],
  );

  const hold = useCallback((item: InboxItem) => {
    const current = serverRef.current.find((entry) => entry.id === item.id);
    if (current) held.current.set(item.id, current);
  }, []);

  const actions: JustAddedActions = useMemo(
    () => ({
      resolve: async (item, request, recurrence, replace) => {
        try {
          const result = await api.resolveJustAdded(item.id, replace ? { ...request, replace } : request);
          if (recurrence && result.task && typeof result.task === "object") {
            await api.setRecurrence((result.task as Task).id, recurrence);
          }
        } catch (err) {
          if (err instanceof ApiError && err.isOffline) {
            postNotice("Filing this needs a connection. Try again once you're back online.");
            return;
          }
          throw err;
        }
        await refresh();
        onFiledRef.current?.();
      },
      retryAi: (item, tier) => {
        hold(item);
        return api.retryJustAdded(item.id, tier);
      },
      refineAi: async (item, body, recurrence, replace) => {
        hold(item);
        let result: InboxAiRetryResult;
        try {
          result = await api.refineJustAdded(item.id, replace ? { ...body, replaces: true } : body);
        } catch (err) {
          if (err instanceof ApiError && err.isOffline) {
            throw new Error("Asking the AI needs a connection. Try again once you're back online.");
          }
          throw err;
        }
        if (recurrence && result.created?.kind === "task") {
          try {
            await api.setRecurrence(result.created.id, recurrence);
          } catch (err) {
            postNotice(
              "The task was created, but its repeat couldn't be set" +
                (err instanceof Error ? `: ${err.message}` : "."),
            );
          }
        }
        return result;
      },
      settled: async (item, result) => {
        held.current.delete(item.id);
        await refresh();
        if (result?.outcome === "filed") onFiledRef.current?.();
      },
    }),
    [hold, refresh],
  );

  return { entries, track, refresh, acknowledge, actions };
}
